import React, { useState, useEffect } from 'react';
import { db } from '../../../utils/firebase';
import { collection, addDoc, updateDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { EmailCampaign, EmailTemplate, Subscriber, RecipientFilter } from '../../../types';
import { ArrowLeft, ArrowRight, Check, Send, Clock, Users, Mail, Eye } from 'lucide-react';

interface CampaignComposerProps {
  campaign: EmailCampaign | null;
  templates: EmailTemplate[];
  onClose: () => void;
}

type Step = 1 | 2 | 3 | 4;

const CampaignComposer: React.FC<CampaignComposerProps> = ({ campaign, templates, onClose }) => {
  const [step, setStep] = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);

  // Form state
  const [name, setName] = useState(campaign?.name || '');
  const [templateId, setTemplateId] = useState(campaign?.templateId || '');
  const [subject, setSubject] = useState(campaign?.subject || '');
  const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>(
    campaign?.recipientFilter || { type: 'all', excludeUnsubscribed: true }
  );
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>(
    campaign?.scheduledAt ? 'later' : 'now'
  );
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const selectedTemplate = templates.find(t => t.id === templateId);

  useEffect(() => {
    fetchSubscriberCount();
  }, [recipientFilter]);

  useEffect(() => {
    if (selectedTemplate && !subject) {
      setSubject(selectedTemplate.subject);
    }
  }, [templateId]);

  const fetchSubscriberCount = async () => {
    try {
      // Always query all subscribers, then filter client-side
      // Firestore where('field', '==', null) only matches docs where field exists and is null
      // Most subscribers don't have unsubscribedAt field at all
      const snapshot = await getDocs(collection(db, 'subscribers'));
      const count = snapshot.docs.filter(doc => {
        const data = doc.data();
        return recipientFilter.excludeUnsubscribed ? !data.unsubscribedAt : true;
      }).length;
      setSubscriberCount(count);
    } catch (error) {
      console.error("Error fetching subscriber count:", error);
    }
  };

  const handleNext = () => {
    if (step < 4) setStep((step + 1) as Step);
  };

  const handleBack = () => {
    if (step > 1) setStep((step - 1) as Step);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return name.trim() && templateId;
      case 2: return subject.trim();
      case 3: return recipientFilter.type && (recipientFilter.type !== 'custom' || (recipientFilter.customEmails?.length ?? 0) > 0);
      case 4: return scheduleType === 'now' || (scheduledDate && scheduledTime);
      default: return false;
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const campaignData = {
        name: name.trim(),
        templateId,
        subject: subject.trim(),
        recipientFilter,
        status: 'draft' as const,
        stats: { totalRecipients: 0, sent: 0, failed: 0, opened: 0, clicked: 0 },
        updatedAt: serverTimestamp(),
        createdBy: 'admin'
      };

      if (campaign) {
        await updateDoc(doc(db, 'emailCampaigns', campaign.id), campaignData);
      } else {
        await addDoc(collection(db, 'emailCampaigns'), {
          ...campaignData,
          createdAt: serverTimestamp()
        });
      }
      onClose();
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSendOrSchedule = async () => {
    setSending(true);
    try {
      const scheduledAt = scheduleType === 'later'
        ? new Date(`${scheduledDate}T${scheduledTime}`)
        : null;

      const campaignData = {
        name: name.trim(),
        templateId,
        subject: subject.trim(),
        recipientFilter,
        status: scheduleType === 'later' ? 'scheduled' as const : 'sending' as const,
        scheduledAt: scheduledAt ? scheduledAt : null,
        stats: { totalRecipients: subscriberCount, sent: 0, failed: 0, opened: 0, clicked: 0 },
        updatedAt: serverTimestamp(),
        createdBy: 'admin'
      };

      let campaignId = campaign?.id;

      if (campaign) {
        await updateDoc(doc(db, 'emailCampaigns', campaign.id), campaignData);
      } else {
        const docRef = await addDoc(collection(db, 'emailCampaigns'), {
          ...campaignData,
          createdAt: serverTimestamp()
        });
        campaignId = docRef.id;
      }

      // If sending now, trigger the cloud function
      if (scheduleType === 'now' && campaignId) {
        try {
          const functions = getFunctions();
          const sendCampaign = httpsCallable(functions, 'sendCampaign');
          await sendCampaign({ campaignId });
        } catch (fnError) {
          console.error("Error triggering send:", fnError);
          // Update status to draft if send fails
          await updateDoc(doc(db, 'emailCampaigns', campaignId), { status: 'draft' });
          alert("Campaign saved but failed to send. Please try again.");
          onClose();
          return;
        }
      }

      onClose();
    } catch (error) {
      console.error("Error sending campaign:", error);
      alert("Failed to send campaign");
    } finally {
      setSending(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              s === step
                ? 'bg-black text-white'
                : s < step
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {s < step ? <Check size={16} /> : s}
          </div>
          {s < 4 && (
            <div className={`w-12 h-0.5 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Campaign Details</h2>
      <p className="text-gray-500 text-sm">Name your campaign and select a template.</p>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
          Campaign Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., January Newsletter"
          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
          Email Template *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
            <div
              key={template.id}
              onClick={() => setTemplateId(template.id)}
              className={`border-2 rounded-sm p-4 cursor-pointer transition-colors ${
                templateId === template.id
                  ? 'border-black bg-gray-50'
                  : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-sm">{template.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{template.category}</p>
                </div>
                {templateId === template.id && (
                  <Check size={20} className="text-green-500" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Email Content</h2>
      <p className="text-gray-500 text-sm">Customize the subject line and preview your email.</p>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
          Subject Line *
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter email subject..."
          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
        />
        <p className="text-xs text-gray-400 mt-1">You can use variables like {'{{firstName}}'}</p>
      </div>

      {selectedTemplate && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
            <Eye size={14} className="inline mr-1" /> Preview
          </label>
          <div className="border border-gray-200 rounded-sm overflow-hidden bg-gray-100">
            <div className="bg-white max-w-xl mx-auto shadow">
              <iframe
                srcDoc={selectedTemplate.htmlContent}
                className="w-full h-80 border-0"
                title="Email Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Recipients</h2>
      <p className="text-gray-500 text-sm">Choose who will receive this campaign.</p>

      <div className="space-y-4">
        <label
          className={`block border-2 rounded-sm p-4 cursor-pointer transition-colors ${
            recipientFilter.type === 'all' ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
          }`}
        >
          <input
            type="radio"
            name="recipientType"
            checked={recipientFilter.type === 'all'}
            onChange={() => setRecipientFilter({ ...recipientFilter, type: 'all' })}
            className="sr-only"
          />
          <div className="flex items-center gap-3">
            <Users size={20} />
            <div>
              <p className="font-bold">All Subscribers</p>
              <p className="text-xs text-gray-500">Send to everyone on your list</p>
            </div>
          </div>
        </label>

        <label
          className={`block border-2 rounded-sm p-4 cursor-pointer transition-colors ${
            recipientFilter.type === 'segment' ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
          }`}
        >
          <input
            type="radio"
            name="recipientType"
            checked={recipientFilter.type === 'segment'}
            onChange={() => setRecipientFilter({ ...recipientFilter, type: 'segment', segment: 'new_subscribers' })}
            className="sr-only"
          />
          <div className="flex items-center gap-3">
            <Users size={20} />
            <div>
              <p className="font-bold">Segment</p>
              <p className="text-xs text-gray-500">Send to a specific group</p>
            </div>
          </div>
        </label>

        {recipientFilter.type === 'segment' && (
          <div className="ml-8">
            <select
              value={recipientFilter.segment || ''}
              onChange={(e) => setRecipientFilter({ ...recipientFilter, segment: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
            >
              <option value="new_subscribers">New Subscribers (last 30 days)</option>
              <option value="engaged">Engaged (opened last email)</option>
              <option value="inactive">Inactive (no opens in 90 days)</option>
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="excludeUnsub"
          checked={recipientFilter.excludeUnsubscribed}
          onChange={(e) => setRecipientFilter({ ...recipientFilter, excludeUnsubscribed: e.target.checked })}
          className="rounded"
        />
        <label htmlFor="excludeUnsub" className="text-sm">Exclude unsubscribed emails</label>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <p className="text-blue-800 text-sm">
          <strong>{subscriberCount}</strong> subscribers will receive this campaign
        </p>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Send Options</h2>
      <p className="text-gray-500 text-sm">Send now or schedule for later.</p>

      <div className="space-y-4">
        <label
          className={`block border-2 rounded-sm p-4 cursor-pointer transition-colors ${
            scheduleType === 'now' ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
          }`}
        >
          <input
            type="radio"
            name="scheduleType"
            checked={scheduleType === 'now'}
            onChange={() => setScheduleType('now')}
            className="sr-only"
          />
          <div className="flex items-center gap-3">
            <Send size={20} />
            <div>
              <p className="font-bold">Send Now</p>
              <p className="text-xs text-gray-500">Start sending immediately</p>
            </div>
          </div>
        </label>

        <label
          className={`block border-2 rounded-sm p-4 cursor-pointer transition-colors ${
            scheduleType === 'later' ? 'border-black bg-gray-50' : 'border-gray-200 hover:border-gray-400'
          }`}
        >
          <input
            type="radio"
            name="scheduleType"
            checked={scheduleType === 'later'}
            onChange={() => setScheduleType('later')}
            className="sr-only"
          />
          <div className="flex items-center gap-3">
            <Clock size={20} />
            <div>
              <p className="font-bold">Schedule</p>
              <p className="text-xs text-gray-500">Choose a date and time</p>
            </div>
          </div>
        </label>

        {scheduleType === 'later' && (
          <div className="ml-8 flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                Date
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                Time
              </label>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 space-y-2">
        <h3 className="font-bold text-sm">Campaign Summary</h3>
        <div className="text-sm space-y-1">
          <p><span className="text-gray-500">Name:</span> {name}</p>
          <p><span className="text-gray-500">Subject:</span> {subject}</p>
          <p><span className="text-gray-500">Template:</span> {selectedTemplate?.name}</p>
          <p><span className="text-gray-500">Recipients:</span> {subscriberCount} subscribers</p>
          <p><span className="text-gray-500">Send:</span> {scheduleType === 'now' ? 'Immediately' : `${scheduledDate} at ${scheduledTime}`}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-bold uppercase tracking-wider">Back</span>
          </button>
          <h2 className="text-xl font-bold">
            {campaign ? 'Edit Campaign' : 'New Campaign'}
          </h2>
        </div>
        <button
          onClick={handleSaveDraft}
          disabled={saving || !name.trim()}
          className="px-4 py-2 text-sm font-bold uppercase tracking-wider border border-gray-300 hover:border-black transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Step Content */}
      <div className="bg-white border border-gray-200 p-6 min-h-[400px]">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border border-gray-300 hover:border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {step < 4 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleSendOrSchedule}
            disabled={sending || !canProceed()}
            className="flex items-center gap-2 px-6 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Processing...' : scheduleType === 'now' ? (
              <>
                <Send size={16} />
                Send Campaign
              </>
            ) : (
              <>
                <Clock size={16} />
                Schedule Campaign
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CampaignComposer;
